import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';

interface WarehouseSubstandardReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;


    filter: {
        dZoneGroups: any;
        targetDates: Date[];
    };

    items: any[];
}

const state: WarehouseSubstandardReportState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        dZoneGroups: null,
        targetDates: [new Date(), new Date()],
    },

    items: [],
};

function buildQuery (filter: any) {
    return {
      columns: {
        client_name: 'c.name',
        deal_expected_delivery_date: 'd.expected_delivery_date',
        deal_id: 'd.id',
        product_name: 'p.name',
        product_container_name: 'pc.name',
      },
      from: 's:deals',
      from_as: 'd',
      joins: [
        {
          join: 's:clients',
          as: 'c',
          on: {
            $eq: [
              'd.client_id',
              'c.id'
            ]
          }
        },
        {
          join: 's:deal_product_containers',
          as: 'dpc',
          on: {
            $eq: [
              'dpc.deal_id',
              'd.id'
            ]
          }
        },
        {
          join: 's:product_containers',
          as: 'pc',
          on: {
            $eq: [
              'pc.id',
              'dpc.product_container_id'
            ]
          }
        },
        {
          join: 's:products',
          as: 'p',
          on: {
            $eq: [
              'p.id',
              'pc.product_id'
            ]
          }
        }
      ],
      where: {
        $and: [
          {
            $eq: [
              'pc.is_substandard',
              {
                $: true
              }
            ]
          },
          ...(
            filter.date_from ? [
              {
                $gte: [
                  'd.expected_delivery_date',
                  {
                    $: filter.date_from
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.date_to ? [
              {
                $lte: [
                  'd.expected_delivery_date',
                  {
                    $: filter.date_to
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.product_id ? [
              {
                $eq: [
                  'p.id',
                  {
                    $: filter.product_id
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.product_container_id ? [
              {
                $eq: [
                  'pc.id',
                  {
                    $: filter.product_container_id
                  }
                ]
              }
            ] : []
          )
        ]
      },
      page_size: 10000,
      page: 1
    };
}

function buildExportQuery(filter: any) {
    return {
      template: '<!DOCTYPE html>\n<table font=Verdana font-size=8>\n  <tr>\n    <td wrap bgcolor=FF72BF44 width=10>Дата</td>\n    <td wrap bgcolor=FF72BF44 width=45>Клиент</td>\n    <td wrap bgcolor=FF72BF44 width=25>Продукт</td>\n  </tr>\n\n  {{#clients.documents}}\n    <tr>\n      <td wrap>{{deal_expected_delivery_date}}</td>\n      <td wrap>{{client_name}}</td>\n      <td wrap>{{product_name}}, {{product_container_name}}</td>\n    </tr>\n  {{/clients.documents}}\n</table>\n',
      queries: {
        clients: buildQuery(filter)
      },
      format: 'xlsx'
    };
}

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).format('YYYY-MM-DD')
    }

    if (params.dZoneGroups) {
        queryParams.delivery_zone_id = params.dZoneGroups;
    }

    if (params.product_id) {
        queryParams.product_id = params.product_id;
    }

    if (params.product_container_id) {
        queryParams.product_container_id = params.product_container_id;
    }

    if (params.sortField !== null) {
        queryParams.order_by = params.sortField;
        queryParams.order_type = params.sortDirection;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<WarehouseSubstandardReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('documents.list')
                        .action(buildQuery(buildFilter(params))),
                ]).then(((result: ServerResult) => {
                    commit('SET_REPORT_DATA', result.getOperation(REPORT_DATA).getContent());
                    commit('LOADED_SUCCESS');

                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

    exportDataAsXLSX({rootState}, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('EXPORT')
                .raw('documents.report')
                .action(buildExportQuery(buildFilter(params)))
            ]).then(((result: ServerResult) => {
                const exportOpContent = result.getOperation('EXPORT').getContent();
                const fToken = exportOpContent.token;
                const aToken = (rootState as any).ubic.token
                const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
                saveAs(fileUrl, 'export.xlsx')

                resolve();
            }));
        })
    },
};

export const mutations: MutationTree<WarehouseSubstandardReportState> = {
    START_LOADING: (state) => {
        // state.loaded = false;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.items = data.documents;
    },
};

const getters = {

};

export const STORE_KEY = '$_warehouse_substandard_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
